import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarCheck, Download, ExternalLink, Globe, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { VendorAvatar } from "@/components/Brand";
import { ChatPanel } from "@/components/ChatPanel";
import { LogoutButton } from "@/components/LogoutButton";
import { QuoteForm, AnswersView } from "@/components/QuoteForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LinkRow = ({ href, icon: Icon, label, testid }: any) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
    className="flex items-center gap-3 rounded-xl border bg-white p-3.5 text-sm font-semibold transition-colors hover:border-emerald-500">
    <Icon className="h-4 w-4 text-emerald-600" /> <span className="flex-1 truncate">{label}</span> <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
  </a>
);

export default function VendorView() {
  const { leadId } = useParams();
  const [params] = useSearchParams();
  const { customer } = useAuth();
  const [d, setD] = useState<any>(null);

  const load = () => api.get(`/me/vendors/${leadId}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    if (customer) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, leadId]);

  if (customer === null) return <Navigate to="/attendee" replace />;
  if (!d) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  const { vendor, assets, form, answers, lead } = d;
  const submitQuote = async (a: any) => {
    try {
      await api.post(`/me/vendors/${leadId}/quote`, { answers: a });
      toast.success(`Sent to ${vendor.name}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="h-24" style={{ background: `linear-gradient(135deg, ${vendor.brand_color || "#E05A33"}, #1C1917)` }}>
        <div className="mx-auto flex max-w-md items-center justify-between px-4 pt-4">
          <Link to="/me" className="inline-flex items-center gap-1 text-sm font-semibold text-white/90" data-testid="vendor-view-back-link"><ArrowLeft className="h-4 w-4" /> My vendors</Link>
          <LogoutButton kind="customer" testid="vendor-view-logout-button" className="text-white/90 hover:bg-white/10 hover:text-white" />
        </div>
      </div>
      <main className="mx-auto -mt-10 max-w-md space-y-5 px-4 pb-12">
        <div className="rise hard-shadow rounded-2xl border bg-white p-5">
          <VendorAvatar vendor={vendor} size={56} />
          <h1 className="mt-3 text-2xl font-extrabold tracking-tight" data-testid="vendor-view-name">{vendor.name}</h1>
          <p className="text-sm text-slate-600">{vendor.tagline}</p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-slate-500">Saved at {lead.event_name}</p>
          {params.get("new") && (
            <p className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-800" data-testid="vendor-view-success-banner">
              <PartyPopper className="h-4 w-4 shrink-0" /> {lead.preregistered && !lead.checked_in ? `You're registered for ${lead.event_name}! Scan ${vendor.name}'s booth QR code at the event to check in.` : `Saved! ${vendor.name} received your details.`}
            </p>
          )}
        </div>
        <Tabs defaultValue="materials">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="materials" data-testid="vendor-view-tab-materials">Materials</TabsTrigger>
            <TabsTrigger value="quote" data-testid="vendor-view-tab-quote">Quote</TabsTrigger>
            <TabsTrigger value="chat" data-testid="vendor-view-tab-chat">Chat</TabsTrigger>
          </TabsList>
          <TabsContent value="materials" className="space-y-2.5 pt-3">
            {vendor.booking_url && <LinkRow href={vendor.booking_url} icon={CalendarCheck} label="Book a call" testid="vendor-view-booking-link" />}
            {(vendor.ctas || []).map((c: any, i: number) => <LinkRow key={`${c.label}-${c.url}`} href={c.url} icon={ExternalLink} label={c.label} testid={`vendor-view-cta-${i}`} />)}
            {assets.map((a: any) => (
              <LinkRow key={a.id} href={a.kind === "file" ? fileUrl(a.file_path) : a.url} icon={a.kind === "file" ? Download : ExternalLink} label={a.title} testid={`vendor-view-asset-${a.id}`} />
            ))}
            {vendor.website && <LinkRow href={vendor.website} icon={Globe} label="Website" testid="vendor-view-website-link" />}
            <div className="flex flex-wrap gap-2 pt-2">
              {Object.entries(vendor.socials || {}).filter(([, v]) => v).map(([k, v]: any) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="rounded-sm border bg-white px-3 py-1.5 text-xs font-semibold capitalize hover:border-emerald-500" data-testid={`vendor-view-social-${k}`}>{k}</a>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="quote" className="space-y-4 pt-3">
            {d.base && (
              <div className="rounded-2xl border bg-white p-5" data-testid="vendor-view-base-answers">
                <AnswersView answers={d.base} testid="vendor-view-base-answers-list" />
                <Link to="/me/profiles" className="mt-3 inline-block text-sm font-semibold text-emerald-700" data-testid="vendor-view-edit-profile-link">Edit my {d.base.event_type_name} answers</Link>
              </div>
            )}
            <div className="rounded-2xl border bg-white p-5">
              {form ? (
                <>
                  <p className="mb-4 text-sm text-slate-600">{answers ? "You've answered — update anytime." : "Answer now or come back later. Your progress isn't shared until you send."}</p>
                  <QuoteForm form={form} initial={answers?.answers} onSubmit={submitQuote} />
                </>
              ) : <p className="text-sm text-slate-500">This vendor hasn't set up a questionnaire yet.</p>}
            </div>
          </TabsContent>
          <TabsContent value="chat" className="pt-3">
            {vendor.chat_enabled ? <ChatPanel url={`/me/vendors/${leadId}/messages`} me="customer" /> : <p className="rounded-2xl border bg-white p-5 text-sm text-slate-500">This vendor follows up by email instead of chat.</p>}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
